import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Icon } from '@iconify/react'
import type { User } from '@supabase/supabase-js'
import { isCloudSyncConfigured, isGoogleAuthProviderEnabled, supabase } from '../auth/supabase'
import { useModalFocus } from './useModalFocus'

type AuthMode = 'login' | 'register' | 'forgot-password'

interface AccountDialogProps {
  open: boolean
  user: User | null
  authReady: boolean
  passwordRecovery: boolean
  syncLabel: string
  onClose: () => void
  onSignOut: () => Promise<void>
  onPasswordRecoveryComplete: () => void
}

const friendlyAuthError = (error: { code?: string; message: string }): string => {
  switch (error.code) {
    case 'invalid_credentials': return 'El correo o la contraseña no son correctos.'
    case 'email_not_confirmed': return 'Confirma tu correo antes de iniciar sesión.'
    case 'user_already_exists': return 'Ya existe una cuenta con ese correo.'
    case 'weak_password': return 'La contraseña no cumple los requisitos de seguridad.'
    case 'same_password': return 'La contraseña nueva debe ser diferente de la anterior.'
    case 'over_email_send_rate_limit': return 'Espera unos minutos antes de pedir otro correo.'
    default: return error.message
  }
}

const passwordRecoveryRedirectUrl = () => {
  const url = new URL(window.location.origin)
  url.searchParams.set('password-recovery', '1')
  return url.toString()
}

const isDisabledGoogleProviderError = (error: { code?: string; message: string }) =>
  error.code === 'validation_failed'
  && /unsupported provider|provider is not enabled/i.test(error.message)

export function AccountDialog({ open, user, authReady, passwordRecovery, syncLabel, onClose, onSignOut, onPasswordRecoveryComplete }: AccountDialogProps) {
  const dialogRef = useRef<HTMLElement>(null)
  const [mode, setMode] = useState<AuthMode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [passwordUpdated, setPasswordUpdated] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [googleAuthEnabled, setGoogleAuthEnabled] = useState(false)

  useModalFocus(open, dialogRef, { onEscape: onClose })

  useEffect(() => {
    if (!open) return
    setPassword('')
    setPasswordConfirmation('')
    setPasswordUpdated(false)
    setMessage(null)
    setError(null)
  }, [open, mode, passwordRecovery])

  useEffect(() => {
    if (!open || user || passwordRecovery || !isCloudSyncConfigured) {
      setGoogleAuthEnabled(false)
      return
    }

    let active = true
    setGoogleAuthEnabled(false)
    void isGoogleAuthProviderEnabled().then((enabled) => {
      if (active) setGoogleAuthEnabled(enabled)
    })
    return () => {
      active = false
    }
  }, [open, passwordRecovery, user])

  if (!open) return null

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setMessage(null)
    if (!supabase) return
    if ((mode === 'register' || passwordRecovery) && password !== passwordConfirmation) {
      setError('Las contraseñas no coinciden.')
      return
    }

    setBusy(true)
    try {
      if (passwordRecovery) {
        const { error: authError } = await supabase.auth.updateUser({ password })
        if (authError) throw authError
        setPasswordUpdated(true)
        setMessage('Contraseña actualizada. Ya puedes continuar con tu carrera.')
      } else if (mode === 'forgot-password') {
        const { error: authError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: passwordRecoveryRedirectUrl(),
        })
        if (authError) throw authError
        setMessage('Si existe una cuenta con ese correo, recibirás un enlace para crear una contraseña nueva.')
      } else if (mode === 'register') {
        const { data, error: authError } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: { emailRedirectTo: window.location.origin },
        })
        if (authError) throw authError
        if (!data.session) {
          setMessage('Cuenta creada. Revisa tu correo y confirma el enlace para iniciar sesión.')
          return
        }
        setMessage('Cuenta creada. Tu cuenta empieza una carrera nueva desde cero.')
      } else {
        const { error: authError } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        })
        if (authError) throw authError
        onClose()
      }
    } catch (caught) {
      const authError = caught instanceof Error ? caught : new Error('No se pudo completar la solicitud.')
      setError(friendlyAuthError(authError))
    } finally {
      setBusy(false)
    }
  }

  const signInWithGoogle = async () => {
    setError(null)
    setMessage(null)
    if (!supabase) return

    setBusy(true)
    try {
      const { error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      })
      if (authError) throw authError
    } catch (caught) {
      const authError = caught instanceof Error ? caught : new Error('No se pudo abrir el acceso con Google.')
      if (isDisabledGoogleProviderError(authError)) {
        setGoogleAuthEnabled(false)
        return
      }
      setError(friendlyAuthError(authError))
    } finally {
      setBusy(false)
    }
  }

  const finishPasswordRecovery = () => {
    onPasswordRecoveryComplete()
    onClose()
  }

  const requestAnotherRecoveryLink = () => {
    onPasswordRecoveryComplete()
    setMode('forgot-password')
  }

  const signOut = async () => {
    setBusy(true)
    setError(null)
    try {
      await onSignOut()
      onClose()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'No se pudo cerrar la sesión.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dialog-backdrop">
      <section ref={dialogRef} className="account-dialog" role="dialog" aria-modal="true" aria-labelledby="account-title" tabIndex={-1}>
        <div className="account-dialog__top"><p className="eyebrow">Progreso entre dispositivos</p><button className="account-dialog__close" type="button" onClick={onClose} aria-label="Cerrar cuenta"><Icon icon="mdi:close" /></button></div>
        <h2 id="account-title">{passwordRecovery ? 'Crea una contraseña nueva' : mode === 'forgot-password' ? 'Recupera tu cuenta' : user ? 'Tu cuenta' : 'Accede a tu cuenta'}</h2>

        {!isCloudSyncConfigured ? <div className="account-dialog__notice" role="status">
          <Icon icon="mdi:cloud-off-outline" />
          <p>La nube aún no está configurada. Tu carrera seguirá guardándose sólo en este dispositivo.</p>
        </div> : passwordRecovery ? !authReady ? <div className="account-dialog__notice" role="status">
          <Icon icon="mdi:shield-sync-outline" />
          <p>Validando el enlace seguro…</p>
        </div> : !user ? <div className="account-dialog__session">
          <p className="account-dialog__error" role="alert">El enlace ha caducado o ya se ha utilizado. Solicita uno nuevo.</p>
          <button className="button account-dialog__secondary" type="button" onClick={requestAnotherRecoveryLink}><Icon icon="mdi:email-refresh-outline" /> Solicitar otro enlace</button>
        </div> : passwordUpdated ? <div className="account-dialog__session">
          <p className="account-dialog__success" role="status">{message}</p>
          <button className="button button--primary" type="button" onClick={finishPasswordRecovery}><Icon icon="mdi:check" /> Volver a tu carrera</button>
        </div> : <form className="account-dialog__form" onSubmit={submit}>
          <p className="account-dialog__intro">Elige una contraseña nueva para <strong>{user.email}</strong>.</p>
          <label>Nueva contraseña<input type="password" autoComplete="new-password" minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} required /><small>Mínimo 8 caracteres.</small></label>
          <label>Repite la contraseña<input type="password" autoComplete="new-password" minLength={8} value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} required /></label>
          {error && <p className="account-dialog__error" role="alert">{error}</p>}
          <button className="button button--primary" type="submit" disabled={busy}>{busy ? 'Guardando…' : 'Guardar contraseña nueva'}</button>
        </form> : user ? <div className="account-dialog__session">
          <div className="account-dialog__identity"><Icon icon="mdi:account-circle-outline" /><span><strong>{user.email}</strong><small>{syncLabel}</small></span></div>
          <p>La carrera y los logros se guardan automáticamente en tu cuenta. Al entrar desde otro dispositivo recuperarás la última copia sincronizada.</p>
          {error && <p className="account-dialog__error" role="alert">{error}</p>}
          <button className="button account-dialog__secondary" type="button" onClick={signOut} disabled={busy}><Icon icon="mdi:logout" /> {busy ? 'Cerrando…' : 'Cerrar sesión'}</button>
        </div> : <>
          {mode !== 'forgot-password' && <div className="account-dialog__tabs" role="tablist" aria-label="Acceso a la cuenta">
            <button type="button" role="tab" aria-selected={mode === 'login'} onClick={() => setMode('login')}>Iniciar sesión</button>
            <button type="button" role="tab" aria-selected={mode === 'register'} onClick={() => setMode('register')}>Crear cuenta</button>
          </div>}
          {mode !== 'forgot-password' && <p className="account-dialog__scope"><Icon icon="mdi:database-sync-outline" aria-hidden="true" /><span>Al entrar, el progreso local de este dispositivo se sustituirá por el de la cuenta. Una cuenta nueva empieza desde cero.</span></p>}
          {mode !== 'forgot-password' && googleAuthEnabled && <div className="account-dialog__oauth">
            <button className="button account-dialog__google" type="button" onClick={signInWithGoogle} disabled={busy}><Icon icon="mdi:google" /> {busy ? 'Abriendo Google…' : 'Continuar con Google'}</button>
            <div className="account-dialog__separator" aria-hidden="true"><span>o con correo</span></div>
          </div>}
          {mode === 'forgot-password' && <div className="account-dialog__recovery-heading">
            <button type="button" onClick={() => setMode('login')}><Icon icon="mdi:arrow-left" /> Volver a iniciar sesión</button>
            <p>Escribe el correo de tu cuenta y te enviaremos un enlace seguro.</p>
          </div>}
          <form className="account-dialog__form" onSubmit={submit}>
            <label>Correo electrónico<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
            {mode !== 'forgot-password' && <label>Contraseña<input type="password" autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} required /><small>Mínimo 8 caracteres.</small></label>}
            {mode === 'register' && <label>Repite la contraseña<input type="password" autoComplete="new-password" minLength={8} value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} required /></label>}
            {mode === 'login' && <button className="account-dialog__forgot" type="button" onClick={() => setMode('forgot-password')}>¿Has olvidado tu contraseña?</button>}
            {error && <p className="account-dialog__error" role="alert">{error}</p>}
            {message && <p className="account-dialog__success" role="status">{message}</p>}
            <button className="button button--primary" type="submit" disabled={busy}>{busy ? 'Procesando…' : mode === 'register' ? 'Crear cuenta' : mode === 'forgot-password' ? 'Enviar enlace de recuperación' : 'Entrar y recuperar progreso'}</button>
          </form>
          <p className="account-dialog__privacy"><Icon icon="mdi:shield-lock-outline" /> Cada usuario sólo puede leer y modificar su propia partida.</p>
        </>}
      </section>
    </div>
  )
}
